import { useId, useState, type ReactNode } from 'react';
import { Icon, IconButton, IconRail, Modal, RailPanel, icons } from '@agentisai/design-system';
import { useViewportTier } from '../hooks/useViewportTier';
import {
  hamburgerBar as hamburgerBarCls,
  main as mainCls,
  railSlot as railSlotCls,
  shell as shellCls,
} from './WorkspaceShell.css';

/** The five canonical rail panels. Only `workstreams` is openable in Drop 5. */
type PanelId = 'workstreams' | 'deliverables' | 'files' | 'agents' | 'memory';

interface RailEntry {
  readonly id: PanelId;
  readonly label: string;
  readonly icon: ReactNode;
  /** "Coming soon" placeholder buttons — focusable but `aria-disabled`. */
  readonly placeholder: boolean;
}

/**
 * Static rail entries (icons + tooltip labels) per the DS `IconRail` story and
 * the agentis2 prototype 1:1. Only Workstreams is wired in Drop 5; the other
 * four are placeholders.
 */
const RAIL_ITEMS: readonly RailEntry[] = [
  {
    id: 'workstreams',
    label: 'Workstreams',
    icon: <Icon as={icons.LayoutGrid} size={20} />,
    placeholder: false,
  },
  {
    id: 'deliverables',
    label: 'Deliverables',
    icon: <Icon as={icons.FileText} size={20} />,
    placeholder: true,
  },
  {
    id: 'files',
    label: 'Files',
    icon: <Icon as={icons.Folder} size={20} />,
    placeholder: true,
  },
  {
    id: 'agents',
    label: 'Agents',
    icon: <Icon as={icons.Users} size={20} />,
    placeholder: true,
  },
  {
    id: 'memory',
    label: 'Workstream Memory',
    icon: <Icon as={icons.Lightbulb} size={20} />,
    placeholder: true,
  },
] as const;

export interface WorkspaceShellProps {
  /**
   * Main pane content — sits to the right of the rail + active panel.
   * The shell itself is layout chrome; the surface it frames (empty state,
   * artifact workspace, etc.) is the consumer's concern.
   */
  readonly children: ReactNode;
  /**
   * Rail-panel body — typically the workstream list/bubbles (C3). When the
   * Workstreams panel is collapsed this is rendered hidden (the panel mounts
   * once and toggles via `hidden`, so React Query state survives a collapse).
   */
  readonly workstreamsPanel?: ReactNode;
  /** Right-aligned action in the Workstreams subheader (e.g. `+ New`, S-C3). */
  readonly workstreamsAction?: ReactNode;
}

/**
 * WorkspaceShell — composes the DS `IconRail` + `RailPanel` into the real
 * authenticated workspace (S-C2).
 *
 * A11y model is the DS disclosure pattern (M11): the active rail button
 * carries `aria-expanded` + `aria-controls` pointing at the panel's id. The
 * four placeholder buttons (Deliverables / Files / Agents / Workstream
 * Memory) are NOT native-disabled — that would suppress pointer + focus and
 * hide their "coming soon" tooltip from keyboard + screen-reader users — they
 * carry `aria-disabled="true"`, stay focusable, and click is a no-op.
 *
 * Only `Workstreams` is openable in Drop 5; expand/collapse is mutually
 * exclusive in the underlying state machine so a future panel slot drops in
 * without a rewrite.
 */
export function WorkspaceShell({
  children,
  workstreamsPanel,
  workstreamsAction,
}: WorkspaceShellProps) {
  // Mutually-exclusive panel state (Drop 5: only `workstreams` ever lands here).
  // Default open — the rail is the primary surface; landing collapsed would
  // hide the create flow C3 wires into the panel.
  const [openPanel, setOpenPanel] = useState<PanelId | null>('workstreams');

  // Stable id per shell instance for `aria-controls` ↔ panel id wiring. Lets
  // two shells coexist (unlikely, but the cost is zero) without colliding ids.
  const panelId = useId();

  const isOpen = openPanel === 'workstreams';

  function toggleWorkstreams() {
    setOpenPanel((prev) => (prev === 'workstreams' ? null : 'workstreams'));
  }

  // S-C4 — compact-tier drawer state. The hamburger button (rendered only at
  // compact via CSS) opens the same workstreamsPanel content inside a Modal
  // using `RailPanel fluid` per the canonical recipe. The Modal's `useDialog`
  // handles focus trap + Escape + body scroll lock (DS contract).
  const tier = useViewportTier();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Derive (don't sync via useEffect → setState — react-hooks lint blocks
  // cascading renders): if the viewport flips to regular the desktop rail
  // owns the panel, so the centred drawer collapses by computation, not by
  // an effect that races the next render.
  const drawerVisible = drawerOpen && tier === 'compact';

  return (
    <div className={shellCls}>
      {/* Rail + panel hidden under `lg` (1024px) — compact-tier nav is C4 (PLAN).
          The slot stays in the DOM at all viewports so React Query / shell
          state doesn't tear when the viewport crosses the breakpoint mid-session.
          Without this, the 48px rail + 280px panel force horizontal overflow at
          375px and the S-F2 e2e fails. */}
      <div className={railSlotCls}>
        <IconRail>
          {RAIL_ITEMS.map((item) => {
            if (item.placeholder) {
              // aria-disabled (not native `disabled`) so the button stays in the
              // tab order + receives focus + dispatches synthetic clicks; the
              // onClick is a no-op so the placeholder doesn't act. The native
              // `title` is the keyboard-reachable tooltip (lucide-react glyphs
              // already carry an aria role; rail label is the accessible name).
              return (
                <IconRail.Button
                  key={item.id}
                  aria-label={`${item.label} (coming soon)`}
                  aria-disabled="true"
                  title={`${item.label} — coming soon`}
                  icon={item.icon}
                  onClick={(e) => {
                    // Mirrors the spec's "remains focusable, click is inert"
                    // behaviour — never advance state from a placeholder.
                    e.preventDefault();
                  }}
                />
              );
            }
            // The Workstreams trigger — the only live disclosure in Drop 5.
            return (
              <IconRail.Button
                key={item.id}
                aria-label={item.label}
                active={isOpen}
                aria-controls={panelId}
                icon={item.icon}
                onClick={toggleWorkstreams}
              />
            );
          })}
        </IconRail>
        {/* Panel stays mounted so its useQuery (C1) keeps its cache across a
          collapse; `hidden` hides it from layout + the a11y tree at once. */}
        <div hidden={!isOpen}>
          <RailPanel
            id={panelId}
            title="Workstreams"
            subheaderLabel="MY WORKSTREAMS"
            subheaderAction={workstreamsAction}
          >
            {workstreamsPanel}
          </RailPanel>
        </div>
      </div>
      <main className={mainCls}>
        {/* Compact-only hamburger — CSS hides it ≥md so it never appears
            alongside the desktop rail. Opens the fluid RailPanel drawer. */}
        <div className={hamburgerBarCls}>
          <IconButton
            icon={<Icon as={icons.Menu} />}
            aria-label="Open workstreams"
            aria-expanded={drawerVisible}
            size="sm"
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
          />
        </div>
        {children}
      </main>
      {/* The compact drawer. Modal owns focus trap / Escape / body scroll
          lock (useDialog), and `flushBody` lets RailPanel reach the dialog
          edges so its own header is the visible chrome (no duplicated
          "Workstreams" title from Modal). aria-label provides the dialog
          name since we suppress Modal's title. */}
      <Modal
        open={drawerVisible}
        onClose={() => setDrawerOpen(false)}
        aria-label="Workstreams"
        flushBody
      >
        <RailPanel
          title="Workstreams"
          subheaderLabel="MY WORKSTREAMS"
          subheaderAction={workstreamsAction}
          fluid
        >
          {workstreamsPanel}
        </RailPanel>
      </Modal>
    </div>
  );
}
WorkspaceShell.displayName = 'WorkspaceShell';
